import { useMemo } from 'react';
import { graphql } from 'react-relay';

import type { Extension } from '@atlassian/forge-ui/web-client/graphql/types';
import type { GetContextTokenOptions } from '@atlassian/forge-ui/custom-ui/iframe/types';

import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { FORGE_MODULE_ATTACHMENT_ACTION } from '../ForgeModuleType.ts';
import type {
	ForgeUIAttachmentActionExtensionDataType,
	ForgeUIExtensionDataType,
} from '../types.ts';
import { getModuleKey } from '../utils/appData.ts';

import type {
	useGetContextTokenMutation$data,
	useGetContextTokenMutation$variables,
} from './__generated__/useGetContextTokenMutation.graphql.ts';

/**
 * Function type for retrieving Forge Context Tokens.
 * Supports optional overrides for embedded macro scenarios.
 */
type GetContextToken = (options?: GetContextTokenOptions) => Promise<string>;

/**
 * Parameters for useGetContextToken hook (object form for clarity when >3 args).
 */
export type UseGetContextTokenParams = {
	extension: Extension;
	extensionData: ForgeUIExtensionDataType;
	localId: string | undefined;
	contextIds: string[];
	cachingEnabled?: boolean;
};

// Cache types and helpers
export type CachedTokenPromise = {
	promise: Promise<string>;
	expiresAt?: number;
	issuedAt: number;
};

class ContextTokenCache {
	private static instance: ContextTokenCache;
	private promiseCache: Map<string, CachedTokenPromise>;

	static getInstance() {
		if (!ContextTokenCache.instance) {
			ContextTokenCache.instance = new ContextTokenCache();
		}
		return ContextTokenCache.instance;
	}

	private constructor() {
		this.promiseCache = new Map<string, CachedTokenPromise>();
	}

	get(key: string): CachedTokenPromise | undefined {
		return this.promiseCache.get(key);
	}

	set(key: string, value: CachedTokenPromise): void {
		this.promiseCache.set(key, value);
	}

	delete(key: string): void {
		this.promiseCache.delete(key);
	}
}

const EXTRA_BUFFER_MS = 30000; // extra 30 seconds buffer for clock skew

const toGraphQLAttachmentActionLocation = (
	location: ForgeUIAttachmentActionExtensionDataType['attachmentActionLocation'],
): 'ACTION' | 'METADATA' => (location === 'action' ? 'ACTION' : 'METADATA');

const toGraphQLAttachmentActionSurface = (
	surface: ForgeUIAttachmentActionExtensionDataType['surface'],
): 'ATTACHMENT_MACRO' | 'ATTACHMENT_PAGE' =>
	surface === 'attachmentMacro' ? 'ATTACHMENT_MACRO' : 'ATTACHMENT_PAGE';

const isAttachmentActionExtensionData = (
	extensionData: ForgeUIExtensionDataType,
): extensionData is ForgeUIAttachmentActionExtensionDataType =>
	extensionData.type === FORGE_MODULE_ATTACHMENT_ACTION &&
	extensionData.attachment !== undefined &&
	extensionData.attachmentActionLocation !== undefined &&
	extensionData.content !== undefined &&
	extensionData.space?.id !== undefined &&
	extensionData.space.key !== undefined &&
	extensionData.surface !== undefined;

// GraphQL mutation to get context token
const UseGetContextTokenMutation = graphql`
	mutation useGetContextTokenMutation(
		$cloudId: ID!
		$input: ConfluenceForgeContextTokenRequestInput!
	) {
		confluence_generateForgeContextToken(cloudId: $cloudId, input: $input) {
			success
			errors {
				message
			}
			forgeContextToken {
				jwt
				expiresAt
				extensionId
			}
		}
	}
`;

/**
 * Hook to generate a context token function for Forge extensions in Confluence
 *
 * @param params - Named parameters: extension, extensionData, localId, contextIds, and optional cachingEnabled
 * @returns A function that can be called to retrieve context tokens
 */
export const useGetContextToken = ({
	extension,
	extensionData,
	localId,
	contextIds,
	cachingEnabled = false,
}: UseGetContextTokenParams): GetContextToken => {
	const { cloudId } = useSessionData();
	const [runMutation] = useMutationInterop<
		useGetContextTokenMutation$data,
		useGetContextTokenMutation$variables
	>(UseGetContextTokenMutation, {
		interopApolloAggClient: true,
	});

	return useMemo(() => {
		let attachmentActionExtensionData: ForgeUIAttachmentActionExtensionDataType | undefined;
		let standardExtensionData: ForgeUIExtensionDataType | undefined;
		if (isAttachmentActionExtensionData(extensionData)) {
			attachmentActionExtensionData = extensionData;
		} else {
			standardExtensionData = extensionData;
		}

		const fetchTokenFromServer = async (
			effectiveContextIds: string[],
			effectiveExtension: Extension,
			effectiveLocalId: string | undefined,
			cache: ContextTokenCache,
			cacheKey: string,
		): Promise<string> => {
			try {
				// Build the input according to the schema
				const input = {
					contextIds: effectiveContextIds,
					extensionSpecificContexts: {
						appVersion: effectiveExtension.appVersion ?? '',
						context: {
							appVersion: effectiveExtension.appVersion,
							environmentId: effectiveExtension.environmentId,
							environmentType: effectiveExtension.environmentType,
							siteUrl: window?.location?.origin,
							extension: {
								location: undefined,
								...(attachmentActionExtensionData
									? {
											attachment: attachmentActionExtensionData.attachment,
											attachmentActionLocation: toGraphQLAttachmentActionLocation(
												attachmentActionExtensionData.attachmentActionLocation,
											),
											attachmentActionSurface: toGraphQLAttachmentActionSurface(
												attachmentActionExtensionData.surface,
											),
										}
									: {}),
								content: extensionData.content
									? {
											id: extensionData.content.id,
											type: extensionData.content.type,
											subtype: extensionData.content.subtype,
											version: standardExtensionData?.content?.version,
											status: standardExtensionData?.content?.status,
										}
									: undefined,
								isEditing: standardExtensionData?.isEditing,
								macro: standardExtensionData?.macro
									? {
											isConfiguring: standardExtensionData.macro.isConfiguring,
											isInserting: standardExtensionData.macro.isInserting,
											layout: standardExtensionData.macro.layout,
										}
									: undefined,
								space: extensionData.space
									? {
											id: extensionData.space.id,
											key: extensionData.space.key,
										}
									: undefined,
								...(standardExtensionData?.template?.id && {
									template: { id: standardExtensionData.template.id },
								}),
								type: extensionData.type,
								selectedText: standardExtensionData?.selectedText,
							},
							localId: effectiveLocalId,
							moduleKey: getModuleKey(effectiveExtension),
							isConfig: standardExtensionData?.isConfig,
							spaceKey: standardExtensionData?.space?.key,
							contentId: standardExtensionData?.content?.id,
							type: extensionData.type,
							content: standardExtensionData?.content
								? {
										id: standardExtensionData.content.id,
										type: standardExtensionData.content.type,
										subtype: standardExtensionData.content.subtype,
										status: standardExtensionData.content.status,
										version: standardExtensionData.content.version,
									}
								: undefined,
							space: standardExtensionData?.space
								? {
										id: standardExtensionData.space.id,
										key: standardExtensionData.space.key,
									}
								: undefined,
							macro: effectiveExtension.properties?.body
								? { body: JSON.stringify(effectiveExtension.properties.body) }
								: null,
						},
						extensionId: effectiveExtension.id,
						extensionType: effectiveExtension.type,
						installationId: effectiveExtension.installationId,
					},
				};

				const { data } = await runMutation({
					variables: {
						cloudId,
						input,
					},
				});

				if (!data?.confluence_generateForgeContextToken?.success) {
					const errors = data?.confluence_generateForgeContextToken?.errors || [];
					const errorMessage = errors.map((e) => e.message).join(', ') || 'Unknown error';
					throw new Error(`Token generation failed: ${errorMessage}`);
				}

				const forgeContextToken = data.confluence_generateForgeContextToken.forgeContextToken;
				if (!forgeContextToken?.jwt) {
					throw new Error('No JWT token received from GraphQL endpoint');
				}

				// Update cache with expiry info if available
				if (cachingEnabled) {
					const cachedEntry = cache.get(cacheKey);
					if (cachedEntry && forgeContextToken.expiresAt) {
						cachedEntry.expiresAt = Number(forgeContextToken.expiresAt);
					}
				}

				return forgeContextToken.jwt;
			} catch (error: any) {
				// Remove from cache on error so next call will retry
				if (cachingEnabled) {
					cache.delete(cacheKey);
				}

				throw new Error(`Failed to get context token: ${error.message}`);
			}
		};

		// Supports both contextIdsOverride and extensionOverride for embedded macros
		async function getForgeContextToken(options?: GetContextTokenOptions): Promise<string> {
			if (!cloudId) {
				throw new Error('CloudId is required but not available');
			}

			const { contextIdsOverride, extensionOverride } = options ?? {};

			// Use override values if provided (for embedded macros), otherwise use parent values
			const effectiveExtension = extensionOverride?.extension ?? extension;
			const effectiveLocalId = extensionOverride?.localId ?? localId;
			const effectiveContextIds = contextIdsOverride ?? contextIds;

			const cacheKey = JSON.stringify({
				id: effectiveLocalId || effectiveExtension.id,
				data: extensionData,
				contextIds: effectiveContextIds,
			});
			const cache = ContextTokenCache.getInstance();
			const cached = cache.get(cacheKey);
			if (cachingEnabled) {
				if (cached) {
					if (typeof cached.expiresAt !== 'number') {
						return cached.promise;
					}
					if (Date.now() + EXTRA_BUFFER_MS < cached.expiresAt) {
						return cached.promise;
					}
				}

				const issuedAt = Date.now();
				const tokenPromise = fetchTokenFromServer(
					effectiveContextIds,
					effectiveExtension,
					effectiveLocalId,
					cache,
					cacheKey,
				);

				cache.set(cacheKey, {
					promise: tokenPromise,
					expiresAt: undefined,
					issuedAt,
				});

				return tokenPromise;
			}

			return fetchTokenFromServer(
				effectiveContextIds,
				effectiveExtension,
				effectiveLocalId,
				cache,
				cacheKey,
			);
		}

		return getForgeContextToken;
	}, [extension, extensionData, localId, cloudId, runMutation, contextIds, cachingEnabled]);
};
